/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import type { ElementNode, LexicalEditor } from 'lexical';
import {
  $createParagraphNode,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isParagraphNode,
  $isRangeSelection,
  $isTextNode,
} from 'lexical';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalEditable } from '@lexical/react/useLexicalEditable';
import {
  $deleteTableColumn__EXPERIMENTAL,
  $deleteTableRow__EXPERIMENTAL,
  $getNodeTriplet,
  $getTableCellNodeFromLexicalNode,
  $getTableColumnIndexFromTableCellNode,
  $getTableNodeFromLexicalNodeOrThrow,
  $getTableRowIndexFromTableCellNode,
  $insertTableColumn__EXPERIMENTAL,
  $insertTableRow__EXPERIMENTAL,
  $isTableCellNode,
  $isTableRowNode,
  $isTableSelection,
  $unmergeCell,
  getTableObserverFromTableElement,
  HTMLTableElementWithWithTableSelectionState,
  TableCellHeaderStates,
  TableCellNode,
  TableRowNode,
  TableSelection,
} from '@lexical/table';
import * as React from 'react';
import {
  ReactPortal,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import invariant from 'shared/invariant';

import useModal from '../../hooks/useModal';
import ColorPicker from '../../ui/ColorPicker';
import AddColumnLeft from 'assets/icons/add-column-left.svg?react';
import AddColumnRight from 'assets/icons/add-column-right.svg?react';
import AddRowAbove from 'assets/icons/add-row-above.svg?react';
import AddRowBelow from 'assets/icons/add-row-below.svg?react';
import ApplyHeaderColumnStyle from 'assets/icons/apply-header-column-style.svg?react';
import ApplyHeaderRowStyle from 'assets/icons/apply-header-row-style.svg?react';
import DeleteColumn from 'assets/icons/delete-column.svg?react';
import DeleteHeaderColumnStyle from 'assets/icons/delete-header-column-style.svg?react';
import DeleteHeaderRowStyle from 'assets/icons/delete-header-row-style.svg?react';
import DeleteRow from 'assets/icons/delete-row.svg?react';
import DeleteTable from 'assets/icons/delete-table.svg?react';
import BackgroundColor from 'assets/icons/background-color.svg?react';
import TableCell from 'assets/icons/table-cell-button.svg?react';
import MergeCells from 'assets/icons/merge-cells.svg?react';
import UnmergeCells from 'assets/icons/unmerge-cells.svg?react';
import styles from './styles.module.scss';
import { useScopedTranslation } from 'application/localization/useScopedTranslation';
import clsx from 'clsx';
import { useSettings } from '../../context/SettingsContext.tsx';
import { PageDesignerContext } from '../../../DesignerNewPage.tsx';

function computeSelectionCount(selection: TableSelection): {
  columns: number;
  rows: number;
} {
  const selectionShape = selection.getShape();
  return {
    columns: selectionShape.toX - selectionShape.fromX + 1,
    rows: selectionShape.toY - selectionShape.fromY + 1,
  };
}

// This is important when merging cells as there is no good way to re-merge weird shapes (a result
// of selecting merged cells and non-merged)
function isTableSelectionRectangular(selection: TableSelection): boolean {
  const nodes = selection.getNodes();
  const currentRows: Array<number> = [];
  let currentRow = null;
  let expectedColumns = null;
  let currentColumns = 0;
  for (let i = 0; i < nodes.length; i++) {
    const node = nodes[i];
    if ($isTableCellNode(node)) {
      const row = node.getParentOrThrow();
      invariant(
        $isTableRowNode(row),
        'Expected CellNode to have a RowNode parent',
      );
      if (currentRow !== row) {
        if (expectedColumns !== null && currentColumns !== expectedColumns) {
          return false;
        }
        if (currentRow !== null) {
          expectedColumns = currentColumns;
        }
        currentRow = row;
        currentColumns = 0;
      }
      const colSpan = node.__colSpan;
      for (let j = 0; j < colSpan; j++) {
        if (currentRows[currentColumns + j] === undefined) {
          currentRows[currentColumns + j] = 0;
        }
        currentRows[currentColumns + j] += node.__rowSpan;
      }
      currentColumns += colSpan;
    }
  }
  return (
    (expectedColumns === null || currentColumns === expectedColumns) &&
    currentRows.every((v) => v === currentRows[0])
  );
}

function $canUnmerge(): boolean {
  const selection = $getSelection();
  if (
    ($isRangeSelection(selection) && !selection.isCollapsed()) ||
    ($isTableSelection(selection) && !selection.anchor.is(selection.focus)) ||
    (!$isRangeSelection(selection) && !$isTableSelection(selection))
  ) {
    return false;
  }
  const [cell] = $getNodeTriplet(selection.anchor);
  return cell.__colSpan > 1 || cell.__rowSpan > 1;
}

function $cellContainsEmptyParagraph(cell: TableCellNode): boolean {
  if (cell.getChildrenSize() !== 1) {
    return false;
  }
  const firstChild = cell.getFirstChildOrThrow();
  return !(!$isParagraphNode(firstChild) || !firstChild.isEmpty());
}

function $selectLastDescendant(node: ElementNode): void {
  const lastDescendant = node.getLastDescendant();
  if ($isTextNode(lastDescendant)) {
    lastDescendant.select();
  } else if ($isElementNode(lastDescendant)) {
    lastDescendant.selectEnd();
  } else if (lastDescendant !== null) {
    lastDescendant.selectNext();
  }
}

function currentCellBackgroundColor(editor: LexicalEditor): null | string {
  return editor.getEditorState().read(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection) || $isTableSelection(selection)) {
      const [cell] = $getNodeTriplet(selection.anchor);
      if ($isTableCellNode(cell)) {
        return cell.getBackgroundColor();
      }
    }
    return null;
  });
}

type TableCellActionMenuProps = Readonly<{
  contextRef: { current: null | HTMLElement };
  onClose: () => void;
  setIsMenuOpen: (isOpen: boolean) => void;
  showColorPickerModal: (
    title: string,
    showModal: (onClose: () => void) => React.JSX.Element,
  ) => void;
  tableCellNode: TableCellNode;
  cellMerge: boolean;
  anchorElem: HTMLElement;
}>;

function TableActionMenu({
  onClose,
  tableCellNode: _tableCellNode,
  setIsMenuOpen,
  contextRef,
  cellMerge,
  showColorPickerModal,
  anchorElem,
}: TableCellActionMenuProps) {
  const [editor] = useLexicalComposerContext();
  const dropDownRef = useRef<HTMLDivElement | null>(null);
  const [tableCellNode, updateTableCellNode] = useState(_tableCellNode);
  const [selectionCounts, updateSelectionCounts] = useState({
    columns: 1,
    rows: 1,
  });
  const [canMergeCells, setCanMergeCells] = useState(false);
  const [canUnmergeCell, setCanUnmergeCell] = useState(false);
  const [backgroundColor, setBackgroundColor] = useState(
    () => currentCellBackgroundColor(editor) || '',
  );
  const [style, setStyle] = useState({ top: 0, left: 0 });

  const i18n = useScopedTranslation('Page.PageDesigner');

  useEffect(() => {
    return editor.registerMutationListener(
      TableCellNode,
      (nodeMutations) => {
        const nodeUpdated =
          nodeMutations.get(tableCellNode.getKey()) === 'updated';

        if (nodeUpdated) {
          editor.getEditorState().read(() => {
            updateTableCellNode(tableCellNode.getLatest());
          });
          setBackgroundColor(currentCellBackgroundColor(editor) || '');
        }
      },
      { skipInitialization: true },
    );
  }, [editor, tableCellNode]);

  useEffect(() => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      // Merge cells
      if ($isTableSelection(selection)) {
        const currentSelectionCounts = computeSelectionCount(selection);
        updateSelectionCounts(computeSelectionCount(selection));
        setCanMergeCells(
          isTableSelectionRectangular(selection) &&
            (currentSelectionCounts.columns > 1 ||
              currentSelectionCounts.rows > 1),
        );
      }
      // Unmerge cell
      setCanUnmergeCell($canUnmerge());
    });
  }, [editor]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropDownRef.current != null &&
        contextRef.current != null &&
        !dropDownRef.current.contains(event.target as Node) &&
        !contextRef.current.contains(event.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    }

    window.addEventListener('click', handleClickOutside);

    return () => window.removeEventListener('click', handleClickOutside);
  }, [setIsMenuOpen, contextRef]);

  useLayoutEffect(() => {
    const targetRect = anchorElem.getBoundingClientRect();
    const popupEl = dropDownRef.current;
    const popupRect = popupEl?.getBoundingClientRect();

    if (popupRect) {
      let top = targetRect.bottom + window.scrollY;
      let left = targetRect.left + window.scrollX;

      if (left + popupRect.width > window.innerWidth) {
        left = window.innerWidth - popupRect.width - 30;
      }

      if (top + popupRect.height > window.innerHeight + window.scrollY) {
        top = targetRect.top + window.scrollY - popupRect.height;
      }

      setStyle({ top, left });
    }
  }, [anchorElem]);

  const clearTableSelection = useCallback(() => {
    editor.update(() => {
      if (tableCellNode.isAttached()) {
        const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
        const tableElement = editor.getElementByKey(
          tableNode.getKey(),
        ) as HTMLTableElementWithWithTableSelectionState;

        if (!tableElement) {
          throw new Error('Expected to find tableElement in DOM');
        }

        const tableSelection = getTableObserverFromTableElement(tableElement);
        if (tableSelection !== null) {
          tableSelection.clearHighlight();
        }

        tableNode.markDirty();
        updateTableCellNode(tableCellNode.getLatest());
      }

      const rootNode = $getRoot();
      rootNode.selectStart();
    });
  }, [editor, tableCellNode]);

  const mergeTableCellsAtSelection = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isTableSelection(selection)) {
        const { columns, rows } = computeSelectionCount(selection);
        const nodes = selection.getNodes();
        let firstCell: null | TableCellNode = null;
        for (let i = 0; i < nodes.length; i++) {
          const node = nodes[i];
          if ($isTableCellNode(node)) {
            if (firstCell === null) {
              node.setColSpan(columns).setRowSpan(rows);
              firstCell = node;
              const isEmpty = $cellContainsEmptyParagraph(node);
              let firstChild;
              if (
                isEmpty &&
                $isParagraphNode((firstChild = node.getFirstChild()))
              ) {
                firstChild.remove();
              }
            } else if ($isTableCellNode(firstCell)) {
              const isEmpty = $cellContainsEmptyParagraph(node);
              if (!isEmpty) {
                firstCell.append(...node.getChildren());
              }
              node.remove();
            }
          }
        }
        if (firstCell !== null) {
          if (firstCell.getChildrenSize() === 0) {
            firstCell.append($createParagraphNode());
          }
          $selectLastDescendant(firstCell);
        }
        onClose();
      }
    });
  };

  const unmergeTableCellsAtSelection = () => {
    editor.update(() => {
      $unmergeCell();
    });
  };

  const insertTableRowAtSelection = useCallback(
    (shouldInsertAfter: boolean) => {
      editor.update(() => {
        $insertTableRow__EXPERIMENTAL(shouldInsertAfter);
        onClose();
      });
    },
    [editor, onClose],
  );

  const insertTableColumnAtSelection = useCallback(
    (shouldInsertAfter: boolean) => {
      editor.update(() => {
        for (let i = 0; i < selectionCounts.columns; i++) {
          $insertTableColumn__EXPERIMENTAL(shouldInsertAfter);
        }
        onClose();
      });
    },
    [editor, onClose, selectionCounts.columns],
  );

  const deleteTableRowAtSelection = useCallback(() => {
    editor.update(() => {
      $deleteTableRow__EXPERIMENTAL();
      onClose();
    });
  }, [editor, onClose]);

  const deleteTableAtSelection = useCallback(() => {
    editor.update(() => {
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);
      tableNode.remove();

      clearTableSelection();
      onClose();
    });
  }, [editor, tableCellNode, clearTableSelection, onClose]);

  const deleteTableColumnAtSelection = useCallback(() => {
    editor.update(() => {
      $deleteTableColumn__EXPERIMENTAL();
      onClose();
    });
  }, [editor, onClose]);

  const toggleTableRowIsHeader = useCallback(() => {
    editor.update(() => {
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);

      const tableRowIndex = $getTableRowIndexFromTableCellNode(tableCellNode);

      const tableRows = tableNode.getChildren();

      if (tableRowIndex >= tableRows.length || tableRowIndex < 0) {
        throw new Error('Expected table cell to be inside of table row.');
      }

      const tableRow = tableRows[tableRowIndex];

      if (!$isTableRowNode(tableRow)) {
        throw new Error('Expected table row');
      }

      tableRow.getChildren().forEach((tableCell) => {
        if (!$isTableCellNode(tableCell)) {
          throw new Error('Expected table cell');
        }

        tableCell.toggleHeaderStyle(TableCellHeaderStates.ROW);
      });

      clearTableSelection();
      onClose();
    });
  }, [editor, tableCellNode, clearTableSelection, onClose]);

  const toggleTableColumnIsHeader = useCallback(() => {
    editor.update(() => {
      const tableNode = $getTableNodeFromLexicalNodeOrThrow(tableCellNode);

      const tableColumnIndex =
        $getTableColumnIndexFromTableCellNode(tableCellNode);

      const tableRows = tableNode.getChildren<TableRowNode>();
      const maxRowsLength = Math.max(
        ...tableRows.map((row) => row.getChildren().length),
      );

      if (tableColumnIndex >= maxRowsLength || tableColumnIndex < 0) {
        throw new Error('Expected table cell to be inside of table row.');
      }

      for (let r = 0; r < tableRows.length; r++) {
        const tableRow = tableRows[r];

        if (!$isTableRowNode(tableRow)) {
          throw new Error('Expected table row');
        }

        const tableCells = tableRow.getChildren();
        if (tableColumnIndex >= tableCells.length) {
          // if cell is outside of bounds for the current row (for example various merge cell cases) we shouldn't highlight it
          continue;
        }

        const tableCell = tableCells[tableColumnIndex];

        if (!$isTableCellNode(tableCell)) {
          throw new Error('Expected table cell');
        }

        tableCell.toggleHeaderStyle(TableCellHeaderStates.COLUMN);
      }

      clearTableSelection();
      onClose();
    });
  }, [editor, tableCellNode, clearTableSelection, onClose]);

  const handleCellBackgroundColor = useCallback(
    (value: string) => {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection) || $isTableSelection(selection)) {
          const [cell] = $getNodeTriplet(selection.anchor);
          if ($isTableCellNode(cell)) {
            cell.setBackgroundColor(value);
          }

          if ($isTableSelection(selection)) {
            const nodes = selection.getNodes();

            for (let i = 0; i < nodes.length; i++) {
              const node = nodes[i];
              if ($isTableCellNode(node)) {
                node.setBackgroundColor(value);
              }
            }
          }
        }
      });
    },
    [editor],
  );

  let mergeCellButton: null | React.JSX.Element = null;
  if (cellMerge) {
    if (canMergeCells) {
      mergeCellButton = (
        <div
          className={clsx(styles.rowContainer, 'item')}
          onClick={() => mergeTableCellsAtSelection()}
        >
          <MergeCells />
          <div>{i18n.t('merge_cells')}</div>
        </div>
      );
    } else if (canUnmergeCell) {
      mergeCellButton = (
        <div
          className={clsx(styles.rowContainer, 'item')}
          onClick={() => unmergeTableCellsAtSelection()}
        >
          <UnmergeCells />
          <div>{i18n.t('unmerge_cells')}</div>
        </div>
      );
    }
  }

  const isDeletionHeaderRowStyles =
    (tableCellNode.__headerState & TableCellHeaderStates.ROW) ===
    TableCellHeaderStates.ROW;

  const isDeletionHeaderColumnStyles =
    (tableCellNode.__headerState & TableCellHeaderStates.COLUMN) ===
    TableCellHeaderStates.COLUMN;

  return createPortal(
    <div
      className={clsx('dropdown', 'absolute')}
      ref={dropDownRef}
      onClick={(e) => {
        e.stopPropagation();
      }}
      style={{
        top: `${style.top}px`,
        left: `${style.left}px`,
      }}
    >
      {mergeCellButton}
      <div
        className={clsx(styles.rowContainer, 'item')}
        onClick={() => insertTableRowAtSelection(false)}
      >
        <AddRowAbove />
        <div>
          {i18n.t('add_row_above', {
            number:
              selectionCounts.rows === 1 ? '' : `${selectionCounts.rows} `,
            ending: selectionCounts.rows === 1 ? '' : 's',
          })}
        </div>
      </div>
      <div
        className={clsx(styles.rowContainer, 'item')}
        onClick={() => insertTableRowAtSelection(true)}
      >
        <AddRowBelow />
        <div>
          {i18n.t('add_row_below', {
            number:
              selectionCounts.rows === 1 ? '' : `${selectionCounts.rows} `,
            ending: selectionCounts.rows === 1 ? '' : 's',
          })}
        </div>
      </div>
      <div className={styles.divider} />
      <div
        className={clsx(styles.rowContainer, 'item')}
        onClick={() => insertTableColumnAtSelection(false)}
      >
        <AddColumnLeft />
        <div>
          {i18n.t('add_column_left', {
            number:
              selectionCounts.columns === 1
                ? ''
                : `${selectionCounts.columns} `,
            ending: selectionCounts.columns === 1 ? '' : 's',
          })}
        </div>
      </div>
      <div
        className={clsx(styles.rowContainer, 'item')}
        onClick={() => insertTableColumnAtSelection(true)}
      >
        <AddColumnRight />
        <div>
          {i18n.t('add_column_right', {
            number:
              selectionCounts.columns === 1
                ? ''
                : `${selectionCounts.columns} `,
            ending: selectionCounts.columns === 1 ? '' : 's',
          })}
        </div>
      </div>
      <div className={styles.divider} />
      <div
        className={clsx(styles.rowContainer, 'item')}
        onClick={() =>
          showColorPickerModal('Cell background color', () => (
            <ColorPicker
              color={backgroundColor}
              onChange={handleCellBackgroundColor}
            />
          ))
        }
      >
        <BackgroundColor />
        <div>{i18n.t('change_background_color')}</div>
      </div>
      <div
        className={clsx(
          styles.rowContainer,
          'item',
          isDeletionHeaderRowStyles && styles.deleteStyles,
        )}
        onClick={() => toggleTableRowIsHeader()}
      >
        {isDeletionHeaderRowStyles ? (
          <DeleteHeaderRowStyle />
        ) : (
          <ApplyHeaderRowStyle />
        )}
        <div>
          {isDeletionHeaderRowStyles
            ? i18n.t('remove_header_row_style')
            : i18n.t('apply_header_row_style')}
        </div>
      </div>
      <div
        className={clsx(
          styles.rowContainer,
          'item',
          isDeletionHeaderColumnStyles && styles.deleteStyles,
        )}
        onClick={() => toggleTableColumnIsHeader()}
      >
        {isDeletionHeaderColumnStyles ? (
          <DeleteHeaderColumnStyle />
        ) : (
          <ApplyHeaderColumnStyle />
        )}
        <div>
          {isDeletionHeaderColumnStyles
            ? i18n.t('remove_header_column_style')
            : i18n.t('apply_header_column_style')}
        </div>
      </div>
      <div className={styles.divider} />
      <div
        className={clsx(styles.rowContainer, 'item', styles.deleteStyles)}
        onClick={() => deleteTableColumnAtSelection()}
      >
        <DeleteColumn />
        <div>{i18n.t('delete_column')}</div>
      </div>
      <div
        className={clsx(styles.rowContainer, 'item', styles.deleteStyles)}
        onClick={() => deleteTableRowAtSelection()}
      >
        <DeleteRow />
        <div>{i18n.t('delete_row')}</div>
      </div>
      <div
        className={clsx(styles.rowContainer, 'item', styles.deleteStyles)}
        onClick={() => deleteTableAtSelection()}
      >
        <DeleteTable />
        <div>{i18n.t('delete_table')}</div>
      </div>
    </div>,
    document.body,
  );
}

function TableCellActionMenuContainer({
  anchorElem,
  cellMerge,
}: {
  anchorElem: HTMLElement;
  cellMerge: boolean;
}): React.JSX.Element {
  const [editor] = useLexicalComposerContext();
  const pageDesignerContext = useContext(PageDesignerContext);

  const menuButtonRef = useRef(null);
  const menuRootRef = useRef(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const [tableCellNode, setTableMenuCellNode] = useState<TableCellNode | null>(
    null,
  );

  const [colorPickerModal, showColorPickerModal] = useModal();

  const { setOption } = useSettings();

  const $moveMenu = useCallback(() => {
    const menu = menuButtonRef.current;
    const selection = $getSelection();
    const nativeSelection = window.getSelection();
    const activeElement = document.activeElement;

    if (selection == null || menu == null) {
      setTableMenuCellNode(null);
      return;
    }

    const rootElement = editor.getRootElement();

    if (
      $isRangeSelection(selection) &&
      rootElement !== null &&
      nativeSelection !== null &&
      rootElement.contains(nativeSelection.anchorNode)
    ) {
      const tableCellNodeFromSelection = $getTableCellNodeFromLexicalNode(
        selection.anchor.getNode(),
      );

      if (tableCellNodeFromSelection == null) {
        setTableMenuCellNode(null);
        return;
      }

      const tableCellParentNodeDOM = editor.getElementByKey(
        tableCellNodeFromSelection.getKey(),
      );

      if (tableCellParentNodeDOM == null) {
        setTableMenuCellNode(null);
        return;
      }

      setTableMenuCellNode(tableCellNodeFromSelection);
    } else if (!activeElement) {
      setTableMenuCellNode(null);
    }
  }, [editor]);

  useEffect(() => {
    return editor.registerUpdateListener(() => {
      editor.getEditorState().read(() => {
        $moveMenu();
      });
    });
  });

  useEffect(() => {
    const menuButtonDOM = menuButtonRef.current as HTMLButtonElement | null;

    if (menuButtonDOM != null && tableCellNode != null) {
      const tableCellNodeDOM = editor.getElementByKey(tableCellNode.getKey());

      if (tableCellNodeDOM != null) {
        const tableCellRect = tableCellNodeDOM.getBoundingClientRect();
        const menuRect = menuButtonDOM.getBoundingClientRect();
        const anchorRect = anchorElem.getBoundingClientRect();

        const top = tableCellRect.top - anchorRect.top + 4;
        const left =
          tableCellRect.right - menuRect.width - 10 - anchorRect.left;

        menuButtonDOM.style.opacity = '1';
        menuButtonDOM.style.transform = `translate(${left}px, ${top}px)`;
      } else {
        menuButtonDOM.style.opacity = '0';
        menuButtonDOM.style.transform = 'translate(-10000px, -10000px)';
      }
    }
  }, [menuButtonRef, tableCellNode, editor, anchorElem]);

  const prevTableCellDOM = useRef(tableCellNode);

  useEffect(() => {
    if (prevTableCellDOM.current !== tableCellNode) {
      setIsMenuOpen(false);
    }

    prevTableCellDOM.current = tableCellNode;
  }, [prevTableCellDOM, tableCellNode]);

  useEffect(() => {
    const closeFunc = () => {
      setIsMenuOpen(false);
    };

    document.body.onscroll = closeFunc;

    return () => {
      document.body.onscroll = null;
    };
  }, []);

  useEffect(() => {
    if (isMenuOpen) {
      setOption('isTableActionsActive', true, false);
    } else {
      setOption('isTableActionsActive', false, false);
    }
  }, [isMenuOpen]);

  if (
    pageDesignerContext?.defaultLanguage !==
    pageDesignerContext?.pageEditorLanguage
  ) {
    return <></>;
  }

  return (
    <div className="table-cell-action-button-container" ref={menuButtonRef}>
      {tableCellNode != null && (
        <>
          <button
            type="button"
            className="table-cell-action-button chevron-down"
            onClick={(e) => {
              e.stopPropagation();
              setIsMenuOpen((prevState) => !prevState);
            }}
            ref={menuRootRef}
          >
            <TableCell className={styles.tableActionButton} />
          </button>
          {colorPickerModal}
          {isMenuOpen && (
            <TableActionMenu
              contextRef={menuRootRef}
              setIsMenuOpen={setIsMenuOpen}
              onClose={() => {
                setIsMenuOpen(false);
                setTableMenuCellNode(null);
              }}
              tableCellNode={tableCellNode}
              cellMerge={cellMerge}
              showColorPickerModal={showColorPickerModal}
              anchorElem={menuButtonRef.current ?? document.body}
            />
          )}
        </>
      )}
    </div>
  );
}

export default function TableActionMenuPlugin({
  anchorElem = document.body,
  cellMerge = false,
}: {
  anchorElem?: HTMLElement;
  cellMerge?: boolean;
}): null | ReactPortal {
  const isEditable = useLexicalEditable();
  return createPortal(
    isEditable ? (
      <TableCellActionMenuContainer
        anchorElem={anchorElem}
        cellMerge={cellMerge}
      />
    ) : null,
    anchorElem,
  );
}
